import axios from 'axios';
import { retryRequest } from '@/utils/apiutils';

// Auto-detect API URL based on current domain
const getApiUrl = () => {
    if (process.env.VUE_APP_API_URL) {
        return process.env.VUE_APP_API_URL;
    }
    
    // For local development, always use localhost:8000
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        return 'http://localhost:8000/api';
    }
    
    // For production, always use oqu.xsada.net API
    if (window.location.hostname === 'kitab.xsada.net') {
        return 'https://oqu.xsada.net/api';
    }
    
    // If running on oqu.xsada.net, use the same domain
    if (window.location.hostname === 'oqu.xsada.net') {
        return `${window.location.protocol}//${window.location.hostname}/api`;
    }
    
    // For development, use relative URL to work with Vue proxy
    return '';
};

const API_URL = getApiUrl();
console.log('API URL configured as:', API_URL);
console.log('Current hostname:', window.location.hostname);

export const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 10000
});

// Explicitly set the Content-Type header
apiClient.defaults.headers.common['Content-Type'] = 'application/json';

console.log('🔧 API Client Configuration:');
console.log('  - Base URL:', API_URL);
console.log('  - Default headers:', apiClient.defaults.headers);
console.log('  - Common headers:', apiClient.defaults.headers.common);
console.log('  - Interceptors count:', apiClient.interceptors.request.handlers.length);

// Request interceptor for adding the auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        console.log('🔍 API Request Interceptor Debug:');
        console.log('  - URL:', config.url);
        console.log('  - Method:', config.method);
        console.log('  - Token in localStorage:', token ? 'Present' : 'Missing');
        console.log('  - Token preview:', token ? token.substring(0, 20) + '...' : 'N/A');
        console.log('  - All localStorage keys:', Object.keys(localStorage));
        console.log('  - Current headers:', config.headers);
        
        if (token) {
            // Set the authorization header explicitly
            config.headers['Authorization'] = `Bearer ${token}`;
            // Also set it in common headers as backup
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            console.log('✅ Request with auth:', config.url, 'Token:', token.substring(0, 10) + '...');
            console.log('  - Updated headers:', config.headers);
            console.log('  - Authorization header set:', config.headers['Authorization'] ? 'Yes' : 'No');
        } else {
            console.log('❌ Request without auth:', config.url);
        }
        return config;
    },
    error => {
        console.error('❌ Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for token refresh
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried refreshing token yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refresh_token');
                if (!refreshToken) {
                    throw new Error('No refresh token available');
                }

                const response = await apiClient.post('/token/refresh/', {
                    refresh: refreshToken
                });

                const { access } = response.data;
                localStorage.setItem('access_token', access);
                console.log('New access token:', access);

                // Update the authorization header
                apiClient.defaults.headers.common['Authorization'] = `Bearer ${access}`;
                originalRequest.headers.Authorization = `Bearer ${access}`;

                return apiClient(originalRequest);
            } catch (refreshError) {
                // If refresh token is invalid, redirect to login
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

export default {
    // Authentication
    login(credentials) {
        return apiClient.post('/token/', credentials);
    },
    register(userData) {
        return apiClient.post('/accounts/register/', userData);
    },
    getUserProfile() {
        return apiClient.get('/accounts/profile/');
    },
    updateUserProfile(userData) {
        return apiClient.patch('/accounts/profile/', userData);
    },

    // Books
    getBooks(params = {}) {
        return retryRequest(() => apiClient.get('/books/', { params }), 3, 1000);
    },
    getBook(id) {
        return apiClient.get(`/books/${id}/`);
    },
    getBookDetail(id) {
        return apiClient.get(`/books/${id}/`);
    },
    createBook(formData) {
        return apiClient.post('/books/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    updateBook(id, formData) {
        return apiClient.patch(`/books/${id}/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    deleteBook(id) {
        return apiClient.delete(`/books/${id}/`);
    },

    readBook(bookId) {
        return apiClient.post(`/books/${bookId}/read/`);
    },

    getReaderMetadata(bookId, sessionId) {
        return apiClient.get(`/books/${bookId}/read/`, {
            params: { session_id: sessionId }
        });
    },

    // Categories
    getCategories() {
        return apiClient.get('/categories/');
    },
    getCategory(slug) {
        return apiClient.get(`/categories/${slug}/`);
    },
    createCategory(data) {
        return apiClient.post('/categories/', data);
    },
    updateCategory(slug, data) {
        return apiClient.patch(`/categories/${slug}/`, data);
    },
    deleteCategory(slug) {
        return apiClient.delete(`/categories/${slug}/`);
    },

    // Authors
    getAuthors() {
        return apiClient.get('/authors/');
    },
    getAuthor(id) {
        return apiClient.get(`/authors/${id}/`);
    },
    createAuthor(data) {
        return apiClient.post('/authors/', data);
    },
    updateAuthor(id, data) {
        return apiClient.patch(`/authors/${id}/`, data);
    },
    deleteAuthor(id) {
        return apiClient.delete(`/authors/${id}/`);
    },

    // Subscriptions
    getPlans() {
        return apiClient.get('/plans/');
    },
    getUserSubscriptions() {
        console.log('=== getUserSubscriptions API Call ===');
        console.log('Making request to: /subscriptions/');
        console.log('Auth token being used:', localStorage.getItem('access_token'));

        return apiClient.get('/subscriptions/')
            .then(response => {
                console.log('=== API Response Details ===');
                console.log('Status:', response.status);
                console.log('Headers:', response.headers);
                console.log('Data:', response.data);
                console.log('Data type:', typeof response.data);
                console.log('Is array:', Array.isArray(response.data));
                console.log('Length:', response.data?.length);

                // If it's paginated, check for results
                if (response.data && typeof response.data === 'object' && 'results' in response.data) {
                    console.log('Paginated response detected');
                    console.log('Results:', response.data.results);
                    console.log('Count:', response.data.count);
                }

                return response;
            })
            .catch(error => {
                console.error('=== API Error ===');
                console.error('Error:', error);
                if (error.response) {
                    console.error('Error status:', error.response.status);
                    console.error('Error data:', error.response.data);
                }
                throw error;
            });
    },
    createSubscription(planId) {
        return apiClient.post('/subscriptions/', { plan_id: planId });
    },
    cancelSubscription(id) {
        return apiClient.post(`/subscriptions/${id}/cancel/`);
    },

    // Analytics
    getBookmarks(bookId) {
        return apiClient.get(`/books/${bookId}/bookmarks/`);
    },
    getReadingSessions() {
        return apiClient.get('/reading-sessions/');
    },
    endReadingSession(id, data) {
        return apiClient.post(`/reading-sessions/${id}/end/`, data);
    },

    // Bookmarks
    deleteBookmark(id) {
        return apiClient.delete(`/bookmarks/${id}/`);
    },

    // Reading Session
    getReadingSession(id) {
        return apiClient.get(`/reading-sessions/${id}/`);
    },

    // Update a reading session
    updateReadingProgress(sessionId, data) {
        return apiClient.patch(`/reading-sessions/${sessionId}/`, data);
    },

    // Download a PDF (this will be a protected endpoint)
    downloadBook(bookId) {
        return apiClient.get(`/books/${bookId}/download/`, {
            responseType: 'blob'
        });
    },

    // Language
    getAvailableLanguages() {
        return apiClient.get('/languages/');
    },

    setUserLanguage(languageCode) {
        return apiClient.post('/language/set/', { language: languageCode });
    },

    // Add this method to your api.js file if it doesn't exist
    getBookData(bookId, sessionId) {
        return apiClient.get(`/books/${bookId}/read/?session_id=${sessionId}`);
    },

    // Get secure read (complete decrypted PDF)
    getSecureRead(bookId, sessionId) {
        return apiClient.get(`/books/${bookId}/secure_read/`, {
            params: { session_id: sessionId },
            responseType: 'arraybuffer'
        });
    },

    // Image-based reading endpoints
    getBookPagesInfo(bookId) {
        console.log('🔍 getBookPagesInfo called with bookId:', bookId);
        console.log('  - Current localStorage token:', localStorage.getItem('access_token') ? 'Present' : 'Missing');
        console.log('  - API client baseURL:', apiClient.defaults.baseURL);
        console.log('  - About to make API call to:', `/books/${bookId}/pages_info/`);
        
        const request = apiClient.get(`/books/${bookId}/pages_info/`);
        
        console.log('  - Request object created:', request);
        return request;
    },
    getBookPage(bookId, pageNumber) {
        return apiClient.get(`/books/${bookId}/page/`, {
            params: { page: pageNumber },
            responseType: 'blob'
        });
    },

    // Advertisements
    getAdvertisements() {
        return apiClient.get('/advertisements/');
    },
    createAdvertisement(data) {
        return apiClient.post('/advertisements/', data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    updateAdvertisement(id, data) {
        return apiClient.patch(`/advertisements/${id}/`, data, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    },
    deleteAdvertisement(id) {
        return apiClient.delete(`/advertisements/${id}/`);
    },
};

// Author endpoints
export function getAuthorDocuments() {
  return apiClient.get('/accounts/documents/');
}
export function uploadDocument(data) {
  console.log('uploadDocument called with data:', data);
  console.log('Data type:', typeof data);
  console.log('Is FormData:', data instanceof FormData);
  
  if (data instanceof FormData) {
    console.log('FormData contents:');
    for (let [key, value] of data.entries()) {
      console.log(`${key}:`, value);
    }
  }
  
  return apiClient.post('/accounts/documents/', data, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }).then(response => {
    console.log('Upload response:', response);
    return response;
  }).catch(error => {
    console.error('Upload error:', error);
    console.error('Error response:', error.response);
    console.error('Error data:', error.response?.data);
    throw error;
  });
}

// Editor endpoints
export function getEditorDocuments() {
  return apiClient.get('/accounts/documents/');
}
export function assignEditor(documentId, editorId) {
  return apiClient.post(`/accounts/documents/${documentId}/assign_to_editor/`, { editor_id: editorId });
}
export function updateDocumentStatus(documentId, status) {
  return apiClient.post(`/accounts/documents/${documentId}/update_status/`, { status });
}
export function downloadDocument(documentId) {
  return apiClient.get(`/accounts/documents/${documentId}/download/`, { responseType: 'blob' });
}
export function leaveReview(documentId, reviewData) {
  return apiClient.post(`/accounts/document-reviews/`, { ...reviewData, document: documentId });
}
export function getAuthors() {
  return apiClient.get('/accounts/author-profiles/');
}
export function getEditors() {
  return apiClient.get('/accounts/editor-profiles/');
}
export function getDocuments() {
  return apiClient.get('/accounts/documents/');
}